import type { MuscleId } from './muscles'

/**
 * Exponential recovery / fatigue model
 * ------------------------------------
 * fatigue(t) = peak × 2^(-Δt / halfLife)
 *
 * peak comes from volume.ts `computePeakFromSets` using per-set weight×reps
 * and history-relative intensity (first session = 10).
 *
 * Half-lives are per-muscle estimates (quads ~72h, hip abductors ~48h, TFL ~36h),
 * inspired by published recovery windows - not clinical constants. See muscles.ts.
 */

import { MUSCLES } from './muscles'

export interface StrainEvent {
  muscleId: MuscleId
  at: number // epoch ms
  peak: number // 0-1 at time `at`
}

/** @deprecated Prefer computePeakFromSets - kept for unit tests of the classic formula. */
export function computePeak(opts: {
  sets: number
  reps: number
  intensity: number // 1-10
}): number {
  const sets = Math.max(1, opts.sets)
  const intensity = Math.min(10, Math.max(1, opts.intensity))
  const volumeFactor = Math.min(1.5, Math.max(0.4, opts.reps / 10))
  const raw = 0.2 + 0.15 * sets * (intensity / 10) * volumeFactor
  return Math.min(1, Math.max(0.2, raw))
}

/** Current fatigue 0-1 for a single strain event. */
export function fatigueAt(event: StrainEvent, now = Date.now()): number {
  const halfLifeMs = MUSCLES[event.muscleId].halfLifeHours * 3600_000
  const age = Math.max(0, now - event.at)
  return event.peak * Math.pow(2, -age / halfLifeMs)
}

/** Merge concurrent history: keep events, evaluate max current fatigue. */
export function muscleFatigue(events: StrainEvent[], muscleId: MuscleId, now = Date.now()): number {
  const mine = events.filter((e) => e.muscleId === muscleId && e.at <= now)
  if (!mine.length) return 0
  let max = 0
  for (const e of mine) {
    max = Math.max(max, fatigueAt(e, now))
  }
  return Math.min(1, max)
}

export function hoursUntilFresh(
  events: StrainEvent[],
  muscleId: MuscleId,
  now = Date.now(),
  threshold = 0.2,
): number {
  const f = muscleFatigue(events, muscleId, now)
  if (f <= threshold) return 0
  const halfLifeH = MUSCLES[muscleId].halfLifeHours
  const hours = halfLifeH * Math.log2(f / threshold)
  return Math.max(0, hours)
}

/** Interpolate grey → ember red */
export function fatigueToColor(fatigue: number): string {
  const [r, g, b] = fatigueRgb(fatigue)
  return `rgb(${r}, ${g}, ${b})`
}

/** Same ramp as fatigueToColor, for APIs that need #RRGGBB. */
export function fatigueToHex(fatigue: number): string {
  const [r, g, b] = fatigueRgb(fatigue)
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`
}

function fatigueRgb(fatigue: number): [number, number, number] {
  const t = Math.min(1, Math.max(0, fatigue))
  const r = Math.round(107 + (232 - 107) * t)
  const g = Math.round(114 + (72 - 114) * t)
  const b = Math.round(128 + (46 - 128) * t)
  return [r, g, b]
}

export function fatigueLabel(fatigue: number): string {
  if (fatigue < 0.08) return 'Ready'
  if (fatigue < 0.35) return 'Recovering'
  if (fatigue < 0.65) return 'Fatigued'
  return 'Very fatigued'
}

export interface FatiguePoint {
  at: number
  /** 0-1 */
  fatigue: number
}

/** Max current fatigue across a coarse muscle group (same idea as mesh heatmap). */
export function groupFatigue(
  events: StrainEvent[],
  muscleIds: readonly MuscleId[],
  at = Date.now(),
): number {
  if (!muscleIds.length) return 0
  let max = 0
  for (const id of muscleIds) {
    max = Math.max(max, muscleFatigue(events, id, at))
  }
  return Math.min(1, max)
}

/**
 * Sample group fatigue over a trailing window for charts.
 * Includes uniform samples plus each relevant event time so workout peaks are not skipped.
 */
export function fatigueHistorySeries(
  events: StrainEvent[],
  muscleIds: readonly MuscleId[],
  opts: { now?: number; windowMs?: number; samples?: number } = {},
): FatiguePoint[] {
  const now = opts.now ?? Date.now()
  const windowMs = opts.windowMs ?? 14 * 86_400_000
  const samples = Math.max(2, opts.samples ?? 48)
  const start = now - windowMs

  const relevant = events.filter(
    (e) => muscleIds.includes(e.muscleId) && e.at <= now && e.at >= start - windowMs,
  )
  const times = new Set<number>([start, now])
  for (let i = 1; i < samples; i += 1) {
    times.add(start + (windowMs * i) / samples)
  }
  for (const e of relevant) {
    if (e.at >= start && e.at <= now) times.add(e.at)
  }

  return [...times]
    .sort((a, b) => a - b)
    .map((at) => ({ at, fatigue: groupFatigue(events, muscleIds, at) }))
}
